import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Barcode, Package, Plus, Check, X, Delete, 
  DollarSign, Tag, Box, History
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminInventory() {
  const { getAuthHeaders } = useAuth();
  const [barcode, setBarcode] = useState('');
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState('');
  const [cost, setCost] = useState('');
  const [price, setPrice] = useState('');
  const [activeField, setActiveField] = useState('quantity');
  const [loading, setLoading] = useState(false);
  const [recentLogs, setRecentLogs] = useState([]);
  const barcodeInputRef = useRef(null);

  useEffect(() => {
    fetchRecentLogs();
    // Focus barcode input on load
    if (barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
  }, []);

  const fetchRecentLogs = async () => {
    try {
      const response = await axios.get(`${API}/products/inventory/logs`, getAuthHeaders());
      setRecentLogs(response.data.slice(0, 10));
    } catch (error) {
      // error handled by toast
    }
  };

  const searchProduct = async () => {
    if (!barcode.trim()) {
      toast.error('Ingresa un código de barras');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/products/barcode/${barcode}`, getAuthHeaders());
      setProduct(response.data);
      setCost(response.data.cost?.toString() || '');
      setPrice(response.data.price?.toString() || '');
      setQuantity('');
      setActiveField('quantity');
      toast.success(`Producto encontrado: ${response.data.name}`);
    } catch (error) {
      toast.error('Producto no encontrado');
      setProduct(null);
    } finally {
      setLoading(false);
    }
  };

  const handleBarcodeKeyDown = (e) => {
    if (e.key === 'Enter') {
      searchProduct();
    }
  };

  const handleKeypadClick = (value) => {
    if (activeField === 'quantity') {
      if (value === 'clear') {
        setQuantity('');
      } else if (value === 'backspace') {
        setQuantity(prev => prev.slice(0, -1));
      } else {
        setQuantity(prev => prev + value);
      }
    } else if (activeField === 'cost') {
      if (value === 'clear') {
        setCost('');
      } else if (value === 'backspace') {
        setCost(prev => prev.slice(0, -1));
      } else {
        setCost(prev => prev + value);
      }
    } else if (activeField === 'price') {
      if (value === 'clear') {
        setPrice('');
      } else if (value === 'backspace') {
        setPrice(prev => prev.slice(0, -1));
      } else {
        setPrice(prev => prev + value);
      }
    }
  };

  const submitInventory = async () => {
    if (!product) {
      toast.error('Primero escanea un producto');
      return;
    }
    
    const qty = parseInt(quantity);
    if (!qty || qty <= 0) {
      toast.error('Ingresa una cantidad válida');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        product_id: product.id,
        quantity: qty,
        cost: cost ? parseFloat(cost) : null,
        price: price ? parseFloat(price) : null,
      };
      
      await axios.post(`${API}/products/inventory`, payload, getAuthHeaders());
      toast.success(`+${qty} unidades agregadas a ${product.name}`);
      
      // Reset form
      setProduct(null);
      setBarcode('');
      setQuantity('');
      setCost('');
      setPrice('');
      fetchRecentLogs();
      
      // Focus barcode input
      if (barcodeInputRef.current) {
        barcodeInputRef.current.focus();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar inventario');
    } finally {
      setLoading(false);
    }
  };

  const clearAll = () => {
    setProduct(null);
    setBarcode('');
    setQuantity('');
    setCost('');
    setPrice('');
    if (barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
  };

  const KeypadButton = ({ value, label, icon: Icon, className = '', span = 1 }) => (
    <button
      onClick={() => handleKeypadClick(value)}
      className={`h-16 rounded-sm bg-zinc-800 hover:bg-zinc-700 text-white text-xl font-semibold 
        flex items-center justify-center transition-all active:scale-95 ${className}
        ${span === 2 ? 'col-span-2' : ''}`}
      data-testid={`keypad-${value}`}
    >
      {Icon ? <Icon className="w-6 h-6" /> : label || value}
    </button>
  );

  const FieldButton = ({ field, label, icon: Icon, value }) => (
    <button
      onClick={() => setActiveField(field)}
      className={`flex-1 h-14 rounded-sm border flex items-center justify-between px-4 transition-colors ${
        activeField === field
          ? 'bg-gold/10 border-gold text-gold'
          : 'bg-zinc-800 border-zinc-700 text-zinc-300 hover:border-zinc-600'
      }`}
    >
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" />
        <span className="text-sm">{label}</span>
      </div>
      <span className="font-mono text-lg">{value || '0'}</span>
    </button>
  );

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-inventory">
        {/* Header */}
        <div>
          <h1 className="font-heading text-3xl text-white tracking-tight">Ingreso de Inventario</h1>
          <p className="text-zinc-500 mt-1">Escanea el código y registra las cantidades</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Barcode & Product Info */}
          <div className="lg:col-span-2 space-y-4">
            {/* Barcode Scanner Input */}
            <Card className="bg-zinc-900/50 border-zinc-800 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Barcode className="w-5 h-5 text-gold" />
                <h3 className="text-sm text-zinc-500 uppercase tracking-widest">
                  Código de Barras
                </h3>
              </div>
              <div className="flex gap-3">
                <Input
                  ref={barcodeInputRef}
                  value={barcode}
                  onChange={(e) => setBarcode(e.target.value.toUpperCase())}
                  onKeyDown={handleBarcodeKeyDown}
                  placeholder="Escanear o escribir código..."
                  className="flex-1 h-14 bg-zinc-800 border-zinc-700 text-white text-xl font-mono tracking-wider"
                  data-testid="barcode-input"
                />
                <Button
                  onClick={searchProduct}
                  disabled={loading}
                  className="h-14 px-8 bg-gold text-black hover:bg-gold-dark"
                  data-testid="search-barcode-btn"
                >
                  Buscar
                </Button>
              </div>
            </Card>

            {/* Product Info */}
            {product ? (
              <Card className="bg-zinc-900/50 border-zinc-800 p-6 border-l-4 border-l-gold">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-heading text-xl text-white">{product.name}</h3>
                    <p className="text-zinc-500 text-sm mt-1">
                      Código: <span className="text-gold font-mono">{product.barcode}</span>
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-zinc-500 uppercase">Stock Actual</p>
                    <p className="font-heading text-3xl text-gold">{product.stock}</p>
                  </div>
                </div>
                
                {/* Field Selectors */}
                <div className="space-y-3 mt-6">
                  <p className="text-xs text-zinc-500 uppercase tracking-widest">Campos a Editar</p>
                  <div className="flex gap-3">
                    <FieldButton field="quantity" label="Cantidad" icon={Box} value={quantity} />
                    <FieldButton field="cost" label="Costo" icon={Tag} value={cost ? `$${cost}` : ''} />
                    <FieldButton field="price" label="Precio" icon={DollarSign} value={price ? `$${price}` : ''} />
                  </div>
                </div>

                {/* Display Current Value */}
                <div className="mt-6 p-4 bg-zinc-800 rounded-sm">
                  <p className="text-xs text-zinc-500 uppercase tracking-widest mb-2">
                    {activeField === 'quantity' ? 'Cantidad a Agregar' : 
                     activeField === 'cost' ? 'Nuevo Costo' : 'Nuevo Precio'}
                  </p>
                  <p className="font-mono text-4xl text-gold">
                    {activeField === 'quantity' 
                      ? (quantity || '0') + ' pzas'
                      : '$' + (activeField === 'cost' ? cost : price || '0')}
                  </p>
                </div>
              </Card>
            ) : (
              <Card className="bg-zinc-900/50 border-zinc-800 p-12 text-center">
                <Package className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-500">Escanea un código de barras para comenzar</p>
              </Card>
            )}
          </div>

          {/* Right Panel - Keypad */}
          <div className="space-y-4">
            <Card className="bg-zinc-900/50 border-zinc-800 p-4">
              <div className="grid grid-cols-3 gap-2">
                <KeypadButton value="7" />
                <KeypadButton value="8" />
                <KeypadButton value="9" />
                <KeypadButton value="4" />
                <KeypadButton value="5" />
                <KeypadButton value="6" />
                <KeypadButton value="1" />
                <KeypadButton value="2" />
                <KeypadButton value="3" />
                <KeypadButton value="." label="." />
                <KeypadButton value="0" />
                <KeypadButton value="backspace" icon={Delete} className="bg-zinc-700" />
                <KeypadButton value="clear" label="C" className="bg-red-500/20 text-red-500 hover:bg-red-500/30" />
                <KeypadButton 
                  value="00" 
                  label="00" 
                  span={2}
                />
              </div>
              
              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-2 mt-4">
                <Button
                  onClick={clearAll}
                  variant="outline"
                  className="h-14 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                  data-testid="clear-all-btn"
                >
                  <X className="w-5 h-5 mr-2" />
                  Cancelar
                </Button>
                <Button
                  onClick={submitInventory}
                  disabled={!product || !quantity || loading}
                  className="h-14 bg-gold text-black hover:bg-gold-dark"
                  data-testid="submit-inventory-btn"
                >
                  <Check className="w-5 h-5 mr-2" />
                  Guardar
                </Button>
              </div>
            </Card>

            {/* Recent Logs */}
            <Card className="bg-zinc-900/50 border-zinc-800 p-4">
              <div className="flex items-center gap-2 mb-3">
                <History className="w-4 h-4 text-zinc-500" />
                <h3 className="text-xs text-zinc-500 uppercase tracking-widest">
                  Últimos Ingresos
                </h3>
              </div>
              <ScrollArea className="h-48">
                {recentLogs.length === 0 ? (
                  <p className="text-zinc-600 text-sm text-center py-4">Sin registros</p>
                ) : (
                  <div className="space-y-2">
                    {recentLogs.map((log) => (
                      <div 
                        key={log.id} 
                        className="flex items-center justify-between p-2 bg-zinc-800/50 rounded-sm"
                      >
                        <div>
                          <p className="text-sm text-white">{log.product_name}</p>
                          <p className="text-xs text-zinc-500">{log.barcode}</p>
                        </div>
                        <span className="text-gold font-semibold">+{log.quantity}</span>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </Card>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
